import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteServiceAccountSecret } from "@/queries/service-account-secret-queries";
import type { PlainSecret } from "@/queries/secret-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import type { ServiceAccountHeaders } from "@/api";

type Props = {
    secret: PlainSecret;
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
    onClose: () => void;
};

export const ServiceAccountDeleteSecretModal: FC<Props> = ({ secret, serviceAccountId, headers, onClose }) => {
    const { toast } = useToast();
    const deleteSecret = useDeleteServiceAccountSecret({ headers });

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            try {
                await deleteSecret.mutateAsync({
                    serviceAccountId,
                    secretId: secret.id,
                });
                onClose();
                toast({
                    title: `${secret.name} was successfully deleted`,
                });
            } catch (e) {
                toast({
                    title: "Failed to delete secret",
                    description: formatError(e),
                });
            }
        },
        [serviceAccountId, secret.id, secret.name, deleteSecret, onClose, toast],
    );

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="service-account-delete-secret-modal-content"
                data-track-location={TrackLocations.DeleteSecretModal}
            >
                <DialogHeader>
                    <DialogTitle>Delete secret</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    <form
                        id="service-account-delete-secret-form"
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-4"
                    >
                        <ErrorMessage
                            error={deleteSecret.error}
                            data-testid="error-message-service-account-secret-deletion-failed"
                        />
                        <Text className="text-base">Are you sure you want to delete this secret?</Text>
                        <div className="rounded-xl border-0.5 border-border-base bg-surface-glass px-6 py-3">
                            <Text className="text-base font-bold">{secret.name}</Text>
                        </div>
                    </form>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline" autoFocus={true}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="destructive"
                        form="service-account-delete-secret-form"
                        loading={deleteSecret.isPending}
                        disabled={deleteSecret.isPending}
                        data-testid="service-account-delete-secret-submit-button"
                        data-tracking-id="confirm-delete-secret-service-account-delete-secret-modal"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
